// packages/engine/src/process-scaffold-diff.ts
//
// Scaffold-level process diff (plan 2026-07-07-process-scaffold-bpmn-diff P3).
// diffScaffolds(before, after, keys) → a UNION scaffold: one structural tree
// containing both sides' nodes, each tagged added | removed | changed (a
// matched-and-equal node carries no tag and renders plain). The union is laid
// out ONCE (bpmnLayout), so a diff never shows two divergent geometries.
//
// Matching is rename-proof and positional-id-free (walker NodeIds shift on any
// insertion): sibling lists align by
//   1. `as`-name anchors (steps named on either side pair by name),
//   2. stable-ID step signatures (module heads resolve through the per-side
//      name→stableId indexes, so renames don't read as edits),
//   3. LCS over the remaining signature sequence.
// Containers recurse; an unmatched branch tags its whole subtree.
//
// Union invariants:
//   - matched nodes keep the AFTER side's nodeId; before-only nodes get a
//     `before:` id prefix (positional ids collide across sides otherwise);
//   - stepIndex is RE-DENSED over the union tree (per-side indices are two
//     colliding spaces) and the original index is preserved in `diff.detail`
//     consumers don't need — scrubber/droplet tooling stays off in diff mode;
//   - `linear` is rebuilt from the union tree (collapsed-`do` body steps are
//     not diffed — a collapsed `do` is one node; body changes belong to the
//     subprocess's own diff), so the union renders through bpmnLayout, not
//     the swimlane placement.

import type { StableId } from "./types.js";
import { pathStableKey } from "./diff/processes.js";
import type {
  ProcessScaffold,
  ScaffoldBranch,
  ScaffoldDiffTag,
  ScaffoldNode,
  ScaffoldStep,
} from "./process-scaffold-types.js";
import type { CompensationFrame, Reentry } from "./process-walker-types.js";
import { isAwaitNodeId } from "./process-walker-types.js";

export interface DiffKeyContext {
  /** Per-side module name → stable id (diff/processes.ts `moduleNameIndex`). */
  readonly beforeModules: ReadonlyMap<string, StableId>;
  readonly afterModules: ReadonlyMap<string, StableId>;
  /** Per-side subprocess/process name → stable id, so a `do X` matches
   *  through a rename of X. Optional — names match verbatim without it. */
  readonly beforeCallables?: ReadonlyMap<string, StableId>;
  readonly afterCallables?: ReadonlyMap<string, StableId>;
}

type Side = "before" | "after";

const BEFORE_PREFIX = "before:";

/** Stable signature for sibling alignment. Two nodes with equal signatures
 *  are match CANDIDATES; per-node fields then decide same vs changed. */
function signature(n: ScaffoldNode, side: Side, keys: DiffKeyContext): string {
  const mods = side === "before" ? keys.beforeModules : keys.afterModules;
  const calls = side === "before" ? keys.beforeCallables : keys.afterCallables;
  switch (n.kind) {
    case "step": {
      if (n.prose) return `prose:${n.noteText ?? ""}`;
      if (n.reentryTarget) return `go:${n.reentryTarget}`;
      if (n.noteText !== undefined && isAwaitNodeId(n.nodeId)) return `await`;
      if (n.noteText !== undefined) return `note:${pathStableKey(n.from, mods)}`;
      return `call:${pathStableKey(n.from, mods)}>${pathStableKey(n.to, mods)}`;
    }
    case "each":
      // Head text stays OUT of the signature — an edited loop head is a
      // CHANGE on the matched container, not a subtree replacement.
      return "each";
    case "branched":
      return `branched:${n.sub}`;
    case "do": {
      const id = calls?.get(n.target);
      return `do:${id ?? `name:${n.target}`}`;
    }
    case "terminator":
      return `term:${n.type}:${n.all ? "all" : ""}`;
  }
}

/** Per-node comparison of a matched pair → change details ("" = equal). */
function changeDetail(b: ScaffoldNode, a: ScaffoldNode, keys: DiffKeyContext): string {
  const parts: string[] = [];
  if (b.kind === "step" && a.kind === "step") {
    const bOwner = pathStableKey(b.from, keys.beforeModules);
    const aOwner = pathStableKey(a.from, keys.afterModules);
    if (bOwner !== aOwner) parts.push(`owner ${b.from.join(".")} → ${a.from.join(".")}`);
    const bTo = pathStableKey(b.to, keys.beforeModules);
    const aTo = pathStableKey(a.to, keys.afterModules);
    if (bTo !== aTo) parts.push(`callee ${b.to.join(".")} → ${a.to.join(".")}`);
    if ((b.args ?? []).join(",") !== (a.args ?? []).join(",")) parts.push("args");
    if ((b.comment ?? "") !== (a.comment ?? "")) parts.push("comment");
    if ((b.noteText ?? "") !== (a.noteText ?? "")) parts.push(`text`);
    if (!!b.modifiers.compensation !== !!a.modifiers.compensation) parts.push("compensation");
    if ((b.asName ?? "") !== (a.asName ?? "")) parts.push(`as ${b.asName ?? "—"} → ${a.asName ?? "—"}`);
    if ((b.direction ?? "") !== (a.direction ?? "")) parts.push("direction");
    if (!!b.modifiers.dist !== !!a.modifiers.dist) parts.push("dist");
  } else if (b.kind === "each" && a.kind === "each") {
    if (b.label !== a.label) parts.push(`head ${b.label} → ${a.label}`);
  } else if (b.kind === "branched" && a.kind === "branched") {
    if (b.label !== a.label) parts.push(`head ${b.label} → ${a.label}`);
    if ((b.mergeAspect ?? "") !== (a.mergeAspect ?? "")) {
      parts.push(`merge ${b.mergeAspect ?? "—"} → ${a.mergeAspect ?? "—"}`);
    }
    if (b.runCount !== a.runCount) parts.push("run count");
  } else if (b.kind === "do" && a.kind === "do") {
    if (b.target !== a.target) parts.push(`target ${b.target} → ${a.target}`);
    if ((b.args ?? []).join(",") !== (a.args ?? []).join(",")) parts.push("args");
  } else if (b.kind === "terminator" && a.kind === "terminator") {
    if ((b.reason ?? "") !== (a.reason ?? "")) parts.push(`reason`);
  }
  return parts.join("; ");
}

/** Deep-tag a subtree (added/removed); before-side ids get namespaced. */
function tagSubtree(n: ScaffoldNode, tag: ScaffoldDiffTag, side: Side): ScaffoldNode {
  const remapId = (id: string) => (side === "before" ? `${BEFORE_PREFIX}${id}` : id);
  switch (n.kind) {
    case "step":
      return { ...n, nodeId: remapId(n.nodeId), diff: tag };
    case "each":
      return {
        ...n,
        nodeId: remapId(n.nodeId),
        children: n.children.map((c) => tagSubtree(c, tag, side)),
        diff: tag,
      };
    case "branched":
      return {
        ...n,
        nodeId: remapId(n.nodeId),
        groups: n.groups.map((g) => ({ ...g, children: g.children.map((c) => tagSubtree(c, tag, side)), diff: tag })),
        diff: tag,
      };
    case "do":
      return {
        ...n,
        nodeId: remapId(n.nodeId),
        ...(n.body ? { body: n.body.map((c) => tagSubtree(c, tag, side)) } : {}),
        diff: tag,
      };
    case "terminator":
      return { ...n, nodeId: remapId(n.nodeId), diff: tag };
  }
}

/** Longest common subsequence over signature arrays → index pairs. */
function lcsPairs(a: readonly string[], b: readonly string[]): [number, number][] {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i]![j] = a[i] === b[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }
  const out: [number, number][] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push([i, j]);
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) i++;
    else j++;
  }
  return out;
}

export function diffScaffolds(
  before: ProcessScaffold,
  after: ProcessScaffold,
  keys: DiffKeyContext,
): ProcessScaffold {
  const ADDED: ScaffoldDiffTag = { status: "added" };
  const REMOVED: ScaffoldDiffTag = { status: "removed" };
  // Matched-pair id translation: a before-side channel entry (reentry, comp
  // chain/drop) that points at a node which SURVIVED must resolve to that
  // node's after id — walker ids are positional and shift on any insertion,
  // so the raw before id may name a completely different after node.
  const matchedIds = new Map<string, string>(); // before nodeId → after nodeId

  function matchPair(b: ScaffoldNode, a: ScaffoldNode): ScaffoldNode {
    matchedIds.set(b.nodeId, a.nodeId);
    const detail = changeDetail(b, a, keys);
    const tag: ScaffoldDiffTag | undefined = detail ? { status: "changed", detail } : undefined;
    if (a.kind === "step" || a.kind === "terminator") {
      return tag ? { ...a, diff: tag } : a;
    }
    if (a.kind === "each" && b.kind === "each") {
      const children = matchLists(b.children, a.children);
      return { ...a, children, ...(tag ? { diff: tag } : {}) };
    }
    if (a.kind === "do" && b.kind === "do") {
      const body = a.body && b.body ? matchLists(b.body, a.body) : a.body;
      return { ...a, ...(body ? { body } : {}), ...(tag ? { diff: tag } : {}) };
    }
    if (a.kind === "branched" && b.kind === "branched") {
      // Branch groups pair in three tiers — parallel branch labels are
      // POSITIONAL (`branch:N`), so labels alone can't anchor:
      //   1. authored labels present on both sides,
      //   2. exact content-signature LCS (insertion-stable),
      //   3. positional zip of the leftovers (content edits diff INSIDE the
      //      pair instead of dropping the whole branch).
      const groupSig = (g: ScaffoldBranch, side: Side) =>
        g.children.map((c) => signature(c, side, keys)).join("|");
      const authored = (l: string) => l !== "" && !/^branch:\d+$/.test(l);
      const pairBtoA = new Map<number, number>();
      const pairedA = new Set<number>();
      // Tier 1 — authored labels, consumed ONE-TO-ONE (duplicate case labels
      // are legal; a shared map entry would pair two before groups onto one
      // after group and silently drop a subtree).
      const aByLabel = new Map<string, number[]>();
      a.groups.forEach((g, j) => {
        if (authored(g.label)) {
          const q = aByLabel.get(g.label) ?? [];
          q.push(j);
          aByLabel.set(g.label, q);
        }
      });
      b.groups.forEach((g, i) => {
        if (!authored(g.label)) return;
        const q = aByLabel.get(g.label);
        const j = q?.shift();
        if (j !== undefined) {
          pairBtoA.set(i, j);
          pairedA.add(j);
        }
      });
      // Tier 2 — exact content-signature LCS over the rest.
      const bFree1 = b.groups.map((_, i) => i).filter((i) => !pairBtoA.has(i));
      const aFree1 = a.groups.map((_, j) => j).filter((j) => !pairedA.has(j));
      for (const [bi, aj] of lcsPairs(
        bFree1.map((i) => groupSig(b.groups[i]!, "before")),
        aFree1.map((j) => groupSig(a.groups[j]!, "after")),
      )) {
        pairBtoA.set(bFree1[bi]!, aFree1[aj]!);
        pairedA.add(aFree1[aj]!);
      }
      // Tier 3 — zip the leftovers in order (inner diff handles the edits).
      const bFree2 = b.groups.map((_, i) => i).filter((i) => !pairBtoA.has(i));
      const aFree2 = a.groups.map((_, j) => j).filter((j) => !pairedA.has(j));
      for (let k = 0; k < Math.min(bFree2.length, aFree2.length); k++) {
        pairBtoA.set(bFree2[k]!, aFree2[k]!);
        pairedA.add(aFree2[k]!);
      }
      const pairAtoB = new Map<number, number>();
      for (const [i, j] of pairBtoA) pairAtoB.set(j, i);

      const groups: ScaffoldBranch[] = a.groups.map((ag, j) => {
        const bi = pairAtoB.get(j);
        if (bi === undefined) {
          return { ...ag, children: ag.children.map((c) => tagSubtree(c, ADDED, "after")), diff: ADDED };
        }
        return { ...ag, children: matchLists(b.groups[bi]!.children, ag.children) };
      });
      b.groups.forEach((bg, i) => {
        if (!pairBtoA.has(i)) {
          groups.push({
            ...bg,
            children: bg.children.map((c) => tagSubtree(c, REMOVED, "before")),
            diff: REMOVED,
          });
        }
      });
      return { ...a, groups, ...(tag ? { diff: tag } : {}) };
    }
    // Kind mismatch under an equal signature cannot happen (kind is part of
    // the signature); keep the after side defensively.
    return a;
  }

  function matchLists(bs: readonly ScaffoldNode[], as_: readonly ScaffoldNode[]): ScaffoldNode[] {
    // Pass 1 — `as`-name anchors pair steps by name across positions.
    const bAs = new Map<string, number>();
    bs.forEach((n, i) => {
      if (n.kind === "step" && n.asName) bAs.set(n.asName, i);
    });
    const anchoredB = new Map<number, number>(); // before idx → after idx
    const anchoredA = new Map<number, number>();
    as_.forEach((n, j) => {
      if (n.kind === "step" && n.asName && bAs.has(n.asName)) {
        const i = bAs.get(n.asName)!;
        anchoredB.set(i, j);
        anchoredA.set(j, i);
      }
    });
    // Pass 2 — LCS over the unanchored remainder's signatures.
    const bSigs = bs.map((n) => signature(n, "before", keys));
    const aSigs = as_.map((n) => signature(n, "after", keys));
    const bFree = bs.map((_, i) => i).filter((i) => !anchoredB.has(i));
    const aFree = as_.map((_, j) => j).filter((j) => !anchoredA.has(j));
    const pairs = lcsPairs(
      bFree.map((i) => bSigs[i]!),
      aFree.map((j) => aSigs[j]!),
    );
    const matchBtoA = new Map<number, number>(anchoredB);
    for (const [bi, aj] of pairs) matchBtoA.set(bFree[bi]!, aFree[aj]!);
    const matchAtoB = new Map<number, number>();
    for (const [i, j] of matchBtoA) matchAtoB.set(j, i);

    // Emit in AFTER order, interleaving removed before-nodes at the position
    // after their nearest matched predecessor (keeps reading order sane).
    const out: ScaffoldNode[] = [];
    let bCursor = 0;
    const flushRemovedThrough = (bLimit: number) => {
      while (bCursor < bLimit) {
        if (!matchBtoA.has(bCursor)) out.push(tagSubtree(bs[bCursor]!, REMOVED, "before"));
        bCursor++;
      }
    };
    as_.forEach((a, j) => {
      const bi = matchAtoB.get(j);
      if (bi !== undefined) {
        flushRemovedThrough(bi);
        bCursor = Math.max(bCursor, bi + 1);
        out.push(matchPair(bs[bi]!, a));
      } else {
        out.push(tagSubtree(a, ADDED, "after"));
      }
    });
    flushRemovedThrough(bs.length);
    return out;
  }

  const unionTree = matchLists(before.tree, after.tree);

  // Re-dense stepIndexes over the union tree and rebuild linear/byIndex.
  const linear: ScaffoldStep[] = [];
  let nextIndex = 0;
  function reindex(nodes: readonly ScaffoldNode[]): ScaffoldNode[] {
    return nodes.map((n) => {
      if (n.kind === "step") {
        const s: ScaffoldStep = { ...n, stepIndex: nextIndex++ };
        linear.push(s);
        return s;
      }
      if (n.kind === "each") return { ...n, children: reindex(n.children) };
      if (n.kind === "branched")
        return { ...n, groups: n.groups.map((g) => ({ ...g, children: reindex(g.children) })) };
      if (n.kind === "do") return n.body ? { ...n, body: reindex(n.body) } : n;
      return n;
    });
  }
  const tree = reindex(unionTree);
  void linear; // superseded — the final linear list is rebuilt after retargetComp

  // Side channels: after's as-is; before-side ids translate MATCHED-FIRST
  // (before nodeId → the surviving node's after id), then to the removed
  // node's `before:`-namespaced id, else DROP — a stale positional id must
  // never resolve against an unrelated after node.
  const beforeIds = new Set<string>();
  (function collect(nodes: readonly ScaffoldNode[]) {
    for (const n of nodes) {
      if (n.nodeId.startsWith(BEFORE_PREFIX)) beforeIds.add(n.nodeId);
      if (n.kind === "each") collect(n.children);
      else if (n.kind === "branched") n.groups.forEach((g) => collect(g.children));
      else if (n.kind === "do" && n.body) collect(n.body);
    }
  })(tree);
  const translate = (id: string): string | null =>
    matchedIds.get(id) ?? (beforeIds.has(`${BEFORE_PREFIX}${id}`) ? `${BEFORE_PREFIX}${id}` : null);

  // Reversible ids never appear in the tree (the container is transparent) —
  // translate a before saga id through any of its MATCHED forward children:
  // `<rev>/item:N/...` maps to an after id whose reversible prefix is the
  // after saga id. A saga with no surviving children keeps the `before:` id.
  const revCache = new Map<string, string>();
  const translateRev = (revId: string): string => {
    const hit = revCache.get(revId);
    if (hit) return hit;
    let out = `${BEFORE_PREFIX}${revId}`;
    for (const [bId, aId] of matchedIds) {
      if (bId.startsWith(`${revId}/item:`)) {
        const m = aId.match(/^(.*\/reversible:\d+)\//);
        if (m) {
          out = m[1]!;
          break;
        }
      }
    }
    revCache.set(revId, out);
    return out;
  };

  // Post-pass: removed compensation nodes carry their BEFORE saga id in
  // modifiers/comp — retarget onto the translated saga so bpmn's comp-band
  // grouping keeps one band per saga.
  function retargetComp(nodes: readonly ScaffoldNode[]): ScaffoldNode[] {
    return nodes.map((n) => {
      if (n.kind === "step") {
        if (n.diff?.status === "removed" && n.modifiers.compensation && n.modifiers.reversibleId) {
          return { ...n, modifiers: { ...n.modifiers, reversibleId: translateRev(n.modifiers.reversibleId) } };
        }
        return n;
      }
      if (n.kind === "terminator") {
        if (n.diff?.status === "removed" && n.comp) {
          return { ...n, comp: { reversibleId: translateRev(n.comp.reversibleId) } };
        }
        return n;
      }
      if (n.kind === "each") return { ...n, children: retargetComp(n.children) };
      if (n.kind === "branched")
        return { ...n, groups: n.groups.map((g) => ({ ...g, children: retargetComp(g.children) })) };
      if (n.kind === "do") return n.body ? { ...n, body: retargetComp(n.body) } : n;
      return n;
    });
  }
  const finalTree = retargetComp(tree);
  const finalLinear: ScaffoldStep[] = [];
  (function relink(nodes: readonly ScaffoldNode[]) {
    for (const n of nodes) {
      if (n.kind === "step") finalLinear.push(n);
      else if (n.kind === "each") relink(n.children);
      else if (n.kind === "branched") n.groups.forEach((g) => relink(g.children));
      else if (n.kind === "do" && n.body) relink(n.body);
    }
  })(finalTree);

  const reentries: Reentry[] = [...after.reentries];
  for (const r of before.reentries) {
    const from = translate(r.fromNodeId);
    if (!from) continue;
    const to = r.toNodeId ? translate(r.toNodeId) : null;
    reentries.push({ ...r, fromNodeId: from, ...(to ? { toNodeId: to } : { toNodeId: undefined }) });
  }
  const compensation: CompensationFrame[] = [...after.compensation];
  for (const f of before.compensation) {
    const chain = f.chain.map(translate).filter((c): c is string => c !== null);
    const drops = f.drops
      .map((d) => ({ from: translate(d.fromNodeId), to: translate(d.toNodeId) }))
      .filter((d): d is { from: string; to: string } => d.from !== null && d.to !== null)
      .map((d) => ({ fromNodeId: d.from, toNodeId: d.to }));
    const fail = translate(f.failNodeId);
    if (chain.length === 0 && drops.length === 0 && !fail) continue;
    compensation.push({
      reversibleId: translateRev(f.reversibleId),
      failNodeId: fail ?? f.failNodeId,
      chain,
      drops,
    });
  }

  return {
    tree: finalTree,
    linear: finalLinear,
    byIndex: new Map(finalLinear.map((s) => [s.stepIndex, s])),
    collapsedGroups: after.collapsedGroups,
    parallelGroups: after.parallelGroups,
    reentries,
    compensation,
    triggers: [
      ...after.triggers,
      ...before.triggers
        .map(translate)
        .filter((t): t is string => t !== null && t.startsWith(BEFORE_PREFIX)),
    ],
    diagnostics: after.diagnostics,
  };
}
